<script setup lang="ts">
import {
  CampaignCreatorsActiveStatusFilter,
  type CampaignDetailsCreator,
  CampaignHashtags,
  CampaignOverviewCreators,
  CampaignOverviewFilterTargetEnum,
  CampaignOverviewPerformance,
  copyTextToClipboard,
  getActiveSocialMetric,
  getDefaultRangeOption,
  ImageIcon,
  NoteComponent,
} from '@metapic-apps/shared'
import { computed, ref, toValue } from 'vue'

import { useCampaignStatsRequest, useGetCampaignRequest, useSetCampaignActivityRequest } from '@/api'
import { useGetCampaignCreatorsCount, useGetCampaignCreatorsListRequest } from '@/api/campaign.api'
import { useSocialMediaCampaignChart, useSocialMediaCampaignMetricTotals } from '@/api/social-media-campaign-performance.api'
import CopyIcon from '@/assets/icons/copy.svg?component'
import DeleteIcon from '@/assets/icons/delete.svg?component'
import EditIcon from '@/assets/icons/edit.svg?component'
import ExternalLinkIcon from '@/assets/icons/external-link.svg?component'
import LinkIcon from '@/assets/icons/link.svg?component'
import MoreIcon from '@/assets/icons/more.svg?component'
import PauseIcon from '@/assets/icons/pause.svg?component'
import PeopleIcon from '@/assets/icons/people.svg?component'
import PlayIcon from '@/assets/icons/play.svg?component'
import CloseIcon from '@/assets/icons/remove.svg?component'
import CostDataRow from '@/components/CampaignListItem/CostDataRow.vue'
import CampaignOverviewBudget from '@/components/CampaignOverview/CampaignOverviewBudget.vue'
import ClicksLimitOverrideModal from '@/components/CampaignOverview/ClicksLimitOverrideModal.vue'
import CostsOverrideModal from '@/components/CampaignOverview/CostsOverrideModal.vue'
import { config, redirectAndLoginAdvertiserUrl } from '@/lib/helpers'
import { defineCampaignEndReason } from '@/lib/helpers/campaign.helpers'
import { CampaignTypeEnum } from '@/lib/types/campaign.types'
import { CampaignStatusEnum } from '@/lib/types/campaigns.types'
import { useTrafficSourcesStore } from '@/stores/traffic-sources'
import { BoxComponent, ButtonComponent, FlexComponent, ImageComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

useTrafficSourcesStore()
const props = defineProps<{ campaignId: number; hasUpdate?: boolean }>()
const emits = defineEmits(['onClose', 'onDelete', 'onRefresh'])
const { data, isLoading, getCampaign } = useGetCampaignRequest(props.campaignId)
const { isLoading: isStatusLoading, setStatus } = useSetCampaignActivityRequest(props.campaignId)
const dateRange = ref<{ startDate: string; endDate: string }>(getDefaultRangeOption())
const activeFilterTarget = ref<CampaignOverviewFilterTargetEnum>(CampaignOverviewFilterTargetEnum.Clicks)

const advertiserId = computed(() => data.value?.advertiserId)

const { data: campaignStats, isLoading: isCampaignStatsLoading } = useCampaignStatsRequest(props.campaignId, dateRange)
const { totals: socialMetricTotals } = useSocialMediaCampaignMetricTotals(advertiserId, props.campaignId, dateRange)
const { points: socialChartPoints, isLoading: isSocialChartLoading } = useSocialMediaCampaignChart(
  advertiserId,
  props.campaignId,
  computed(() => getActiveSocialMetric(activeFilterTarget.value)),
  dateRange,
)

const creatorsActiveFilter = ref<CampaignCreatorsActiveStatusFilter>(CampaignCreatorsActiveStatusFilter.Active)
const creatorsSearchQuery = ref('')
const creatorsCurrentPage = ref(1)
const creatorsSortField = ref('clicks')
const creatorsSortOrder = ref<'asc' | 'desc'>('desc')
const creatorsCurrency = computed(() => data.value?.currency)

const {
  data: creatorsList,
  totalPages: creatorsTotalPages,
  isLoading: isCreatorsListLoading,
  fetchCreatorsList,
} = useGetCampaignCreatorsListRequest({
  id: props.campaignId,
  status: creatorsActiveFilter,
  currency: creatorsCurrency,
  dateRange,
  page: creatorsCurrentPage,
  searchQuery: creatorsSearchQuery,
  sortBy: { field: creatorsSortField, order: creatorsSortOrder },
})
const { data: creatorsCountStats, isLoading: isCreatorsStatsLoading } = useGetCampaignCreatorsCount({
  id: props.campaignId,
  dateRange,
})

const currentCreator = ref<CampaignDetailsCreator | null>(null)
const isOverrideClickLimitModalOpen = ref(false)
const isOverrideCostsModalOpen = ref(false)

const openOverrideClickLimitModal = (creator: CampaignDetailsCreator) => {
  currentCreator.value = creator
  isOverrideClickLimitModalOpen.value = true
}
const openOverrideCostsModal = (creator: CampaignDetailsCreator) => {
  currentCreator.value = creator
  isOverrideCostsModalOpen.value = true
}

const isCampaignActive = computed(
  () => data.value?.extra?.status === CampaignStatusEnum.Active || data.value?.extra?.status === CampaignStatusEnum.Scheduled,
)
const isPauseBtnShown = computed(
  () =>
    data.value?.extra?.status &&
    [CampaignStatusEnum.Active, CampaignStatusEnum.Paused, CampaignStatusEnum.Scheduled].includes(data.value?.extra?.status),
)

const setCampaignActiveStatus = async () => {
  const shouldPause = isCampaignActive.value
  const newStatus = await setStatus(shouldPause)
  emits('onRefresh')
  if (data.value && newStatus && data.value.extra) {
    data.value.extra.status = newStatus
  }
}

const endReason = computed((): string => {
  const campaign = toValue(data.value)
  if (campaign) {
    return defineCampaignEndReason(campaign)
  }
  return ''
})
</script>
<template>
  <div class="campaign-overview-content__container">
    <LoadingComponent v-if="isLoading" :size="48" />
    <template v-if="!isLoading && data">
      <FlexComponent direction="column">
        <FlexComponent direction="column" class="campaign-overview__header">
          <ButtonComponent class="campaign-overview__close-btn" variant="transparent" is-icon @click="emits('onClose')">
            <el-icon :size="24" color="var(--secondary-64)">
              <CloseIcon />
            </el-icon>
          </ButtonComponent>
          <BoxComponent :mb="24">
            <template v-for="item in data.advertiserList" :key="item.id">
              <BoxComponent :mb="8">
                <router-link :to="`/advertisers/${item.id}`">
                  <FlexComponent :gap="8" align="center">
                    <ImageComponent
                      v-if="item.logo"
                      :image="item.logo"
                      :width="64"
                      :alt="item.label"
                      :height="32"
                      object-fit="contain"
                      resize-type="fit"
                    />
                    <TypographyComponent variant="label-medium">{{ item.label }}</TypographyComponent>
                  </FlexComponent>
                </router-link>
              </BoxComponent>
            </template>
            <TypographyComponent variant="headline-large">{{ data.internalName }}</TypographyComponent>
          </BoxComponent>
          <BoxComponent v-if="data.hashtags?.length" :mb="24">
            <CampaignHashtags :hashtags="data.hashtags" />
          </BoxComponent>
          <BoxComponent v-if="endReason" :mb="24">
            <NoteComponent> {{ endReason }} </NoteComponent>
          </BoxComponent>
          <BoxComponent>
            <FlexComponent align="center" :gap="8" wrap="wrap">
              <ButtonComponent
                v-if="data.campaignType !== CampaignTypeEnum.Standard"
                variant="primary"
                :href="redirectAndLoginAdvertiserUrl(data.advertiserId, `/campaign-users/${data.extra?.id}`)"
                :class="hasUpdate ? 'campaign-overview-content__updates-indicator' : undefined"
                target="_blank"
              >
                <template #iconLeft>
                  <el-icon :size="24">
                    <PeopleIcon />
                  </el-icon>
                </template>
                Manage
                <template #iconRight>
                  <el-icon :size="16">
                    <ExternalLinkIcon />
                  </el-icon>
                </template>
              </ButtonComponent>
              <el-tooltip v-if="!endReason" placement="bottom" effect="light" trigger="click" :auto-close="1000">
                <ButtonComponent
                  variant="primary"
                  @click="() => copyTextToClipboard(`${config.creatorAppUrl}/campaign-invitation?join-campaign=${data?.extra?.token}`)"
                >
                  <template #iconLeft>
                    <el-icon :size="24" color="var(--inverted)">
                      <LinkIcon />
                    </el-icon>
                  </template>
                  Copy invite link
                </ButtonComponent>
                <template #content> Copied! </template>
              </el-tooltip>
              <router-link
                v-if="data?.extra?.id"
                :to="{ name: 'contentLibrary', query: { campaignId: data.extra?.id, campaignName: data.internalName } }"
              >
                <ButtonComponent variant="primary">
                  <template #iconLeft>
                    <el-icon :size="24">
                      <ImageIcon />
                    </el-icon>
                  </template>
                  Content library
                </ButtonComponent>
              </router-link>
              <el-dropdown trigger="click" :teleported="false" placement="bottom-start">
                <ButtonComponent variant="secondary" is-icon>
                  <el-icon :size="24">
                    <MoreIcon />
                  </el-icon>
                </ButtonComponent>
                <template #dropdown>
                  <BoxComponent position="relative" :mt="8" :mb="8">
                    <el-tooltip v-if="endReason" placement="right" effect="light">
                      <el-dropdown-item disabled>
                        <FlexComponent :gap="8" align="center">
                          <el-icon :size="24">
                            <PlayIcon />
                          </el-icon>
                          <TypographyComponent variant="body-large"> Un-pause </TypographyComponent>
                        </FlexComponent>
                      </el-dropdown-item>
                      <template #content>
                        <BoxComponent w="250px">
                          <TypographyComponent variant="body-medium">
                            {{ endReason }}
                          </TypographyComponent>
                        </BoxComponent>
                      </template>
                    </el-tooltip>
                    <div v-else-if="isPauseBtnShown">
                      <el-dropdown-item :disabled="isStatusLoading" @click="setCampaignActiveStatus">
                        <FlexComponent :gap="8" align="center">
                          <el-icon :size="24">
                            <PauseIcon v-if="isCampaignActive" />
                            <PlayIcon v-else />
                          </el-icon>
                          <TypographyComponent variant="body-large"> {{ isCampaignActive ? 'Pause' : 'Un-pause' }} </TypographyComponent>
                        </FlexComponent>
                      </el-dropdown-item>
                    </div>
                    <router-link :to="`/campaigns/${data.extra?.id}`">
                      <el-dropdown-item>
                        <FlexComponent :gap="8" align="center">
                          <el-icon :size="24">
                            <EditIcon />
                          </el-icon>
                          <TypographyComponent variant="body-large"> Edit </TypographyComponent>
                        </FlexComponent>
                      </el-dropdown-item>
                    </router-link>
                    <router-link :to="`/campaigns/copy?id=${data.extra?.id}`">
                      <el-dropdown-item>
                        <FlexComponent :gap="8" align="center">
                          <el-icon :size="24">
                            <CopyIcon />
                          </el-icon>
                          <TypographyComponent variant="body-large"> Copy </TypographyComponent>
                        </FlexComponent>
                      </el-dropdown-item>
                    </router-link>
                    <div>
                      <el-dropdown-item @click="emits('onDelete', data.extra?.id)">
                        <FlexComponent :gap="8" align="center">
                          <el-icon :size="24" color="var(--error)">
                            <DeleteIcon />
                          </el-icon>
                          <TypographyComponent variant="body-large" color="var(--error)"> Delete </TypographyComponent>
                        </FlexComponent>
                      </el-dropdown-item>
                    </div>
                  </BoxComponent>
                </template>
              </el-dropdown>
            </FlexComponent>
          </BoxComponent>
        </FlexComponent>
        <BoxComponent v-if="data.sharedBudget" :mb="24">
          <CampaignOverviewBudget :budget-id="data.sharedBudget.id" :advertiser-id="data.advertiserId" :on-delete="getCampaign" />
        </BoxComponent>
        <BoxComponent :mb="24">
          <CampaignOverviewPerformance
            v-model:date-range="dateRange"
            v-model:active-filter-target="activeFilterTarget"
            view-mode="admin"
            :currency="data.currency"
            :campaign-stats="campaignStats"
            :is-campaign-stats-loading="isCampaignStatsLoading"
            :social-metric-totals="socialMetricTotals"
            :social-chart-points="socialChartPoints"
            :is-social-chart-loading="isSocialChartLoading"
          />
        </BoxComponent>
        <BoxComponent :mb="24">
          <BoxComponent :mb="16">
            <TypographyComponent variant="title-large"> Creators </TypographyComponent>
          </BoxComponent>
          <CampaignOverviewCreators
            v-if="data.extra?.id"
            v-model:active-filter="creatorsActiveFilter"
            v-model:search-query="creatorsSearchQuery"
            v-model:current-page="creatorsCurrentPage"
            v-model:sort-field="creatorsSortField"
            v-model:sort-order="creatorsSortOrder"
            :creators-list="creatorsList"
            :is-list-loading="isCreatorsListLoading"
            :is-stats-loading="isCreatorsStatsLoading"
            :total-pages="creatorsTotalPages"
            :count-stats="creatorsCountStats"
            :currency="data.currency"
            :max-clicks="data.maxClicks"
            :traffic-source-costs="data.trafficSourceCosts"
            view-mode="admin"
          >
            <template #creator-name="{ creator }">
              <router-link :to="`/creators/${creator.userId}/info`">
                <TypographyComponent variant="title-medium" text-truncate>{{ creator.displayName || '' }}</TypographyComponent>
              </router-link>
            </template>
            <template #clicks-action="{ creator }">
              <ButtonComponent
                is-icon
                variant="transparent"
                size="small"
                :icon-size="18"
                icon-color="var(--primary)"
                @click="() => openOverrideClickLimitModal(creator)"
              >
                <EditIcon />
              </ButtonComponent>
            </template>
            <template #costs-action="{ creator }">
              <ButtonComponent
                is-icon
                variant="transparent"
                size="small"
                :icon-size="16"
                icon-color="var(--primary)"
                @click="() => openOverrideCostsModal(creator)"
              >
                <EditIcon />
              </ButtonComponent>
            </template>
            <template #costs-breakdown="{ costs, currency }">
              <CostDataRow :currency="currency" :grouped-traffic-source-costs="costs" wrap="nowrap" hide-labels />
            </template>
          </CampaignOverviewCreators>
        </BoxComponent>
      </FlexComponent>
      <ClicksLimitOverrideModal
        v-if="currentCreator && isOverrideClickLimitModalOpen"
        v-model="isOverrideClickLimitModalOpen"
        :participant-id="currentCreator.id"
        :campaign-id="campaignId"
        :current-clicks="currentCreator.clicksCount"
        :individual-click-limit="currentCreator.overrides.clickLimit"
        :campaign-click-limit="data.maxClicks"
        :campaign-per-user-limit="data.perUserLimit"
        @on-success="fetchCreatorsList"
      />
      <CostsOverrideModal
        v-if="currentCreator && isOverrideCostsModalOpen"
        v-model="isOverrideCostsModalOpen"
        :participant-id="currentCreator.id"
        :campaign-id="campaignId"
        :individual-costs="currentCreator.overrides.trafficSourcesCosts"
        :campaign-costs="data.trafficSourceCosts"
        :currency="data.currency"
        @on-success="fetchCreatorsList"
      />
    </template>
  </div>
</template>
<style lang="scss" scoped>
.campaign-overview-content__container {
  position: relative;
  height: 100%;
}

.campaign-overview-content__updates-indicator {
  position: relative;

  &:after {
    content: '';
    position: absolute;
    right: 16px;
    top: 8px;
    width: 8px;
    height: 8px;
    border-radius: 16px;
    background: #ea4f85;
  }
}

.campaign-overview__header {
  padding: 24px 0;
  margin-top: -24px;
  background: var(--inverted);
  position: sticky;
  top: -24px;
  z-index: 11;
}

.campaign-overview__close-btn {
  position: absolute;
  top: 16px;
  right: -8px;
}

:deep(.el-dropdown-menu__item) {
  padding: 16px;
}

:deep(.el-icon) {
  path {
    fill: currentColor;
  }
}
</style>
